import { forwardRef } from "react";

// Local alias to match React's internal `CrossOrigin` type.
type CrossOrigin = "anonymous" | "use-credentials" | "" | undefined;
import { AlertTriangle, MapPin, PawPrint, Phone, Calendar } from "lucide-react";
import type { Shelter } from "@/types/shelter";
import { isUrgentNeedsActive } from "@/types/shelter";
import shareCardBg from "@/assets/share-card-bg.png";

interface MultiShelterShareCardProps {
  shelters: Shelter[];
  hideDisclaimer?: boolean;
  customHeadline?: string;
  sizeMode?: "post" | "story" | "compact";
  needsFontSize?: "small" | "medium" | "large";
  /**
   * Prefer passing a data URL on mobile exports (iOS Safari + html-to-image).
   * This avoids background images being dropped during rasterization.
   */
  backgroundDataUrl?: string;
}

// Size constants
const CARD_WIDTH = 1080;
const CARD_HEIGHT_POST = 1350;  // 4:5 for FB/IG posts
const CARD_HEIGHT_STORY = 1920; // 9:16 for IG stories

// Truncate text by total character count (preserving paragraph structure)
const truncateText = (
  text: string,
  maxChars: number
): { text: string; hasMore: boolean } => {
  if (!text) return { text: "Brak informacji o potrzebach", hasMore: false };

  // If within limit, return as-is
  if (text.length <= maxChars) return { text, hasMore: false };

  // Truncate at character limit, but try to break at word boundary
  const truncated = text.slice(0, maxChars);
  const lastSpace = truncated.lastIndexOf(" ");
  const lastNewline = truncated.lastIndexOf("\n");
  
  // Prefer breaking at newline if close to end, otherwise at space
  const breakPoint = lastNewline > maxChars * 0.7 ? lastNewline : 
                     lastSpace > maxChars * 0.5 ? lastSpace : maxChars;
  
  return { text: truncated.slice(0, breakPoint).trim() + "…", hasMore: true };
};

// Get max characters based on shelter count - increased for better content display
// For compact mode, return Infinity to show all content
const getMaxCharsForLayout = (shelterCount: number, isStory: boolean = false, isCompact: boolean = false): number => {
  if (isCompact) return Infinity; // No truncation in compact mode - card grows to fit
  if (shelterCount === 1) return isStory ? 600 : 500;
  if (shelterCount === 2) return isStory ? 350 : 280;
  return isStory ? 250 : 200;
};

// Font sizes for different layouts - MUCH larger for single shelter
const FONT_SIZES = {
  single: {
    shelterName: 44,
    location: 34,
    contact: 28,
    needsLabel: 28,
    needsText: 36,
    cta: 30,
    headline: 64,
  },
  double: {
    shelterName: 36,
    location: 28,
    contact: 28,
    needsLabel: 26,
    needsText: 30,
    cta: 28,
    headline: 58,
  },
  triple: {
    shelterName: 26,
    location: 22,
    contact: 22,
    needsLabel: 20,
    needsText: 24,
    cta: 24,
    headline: 48,
  },
};

// Larger font sizes for Story format (9:16) - MUCH larger for single shelter
const FONT_SIZES_STORY = {
  single: {
    shelterName: 52,
    location: 38,
    contact: 32,
    needsLabel: 32,
    needsText: 40,
    cta: 34,
    headline: 76,
  },
  double: {
    shelterName: 42,
    location: 32,
    contact: 32,
    needsLabel: 30,
    needsText: 34,
    cta: 32,
    headline: 68,
  },
  triple: {
    shelterName: 32,
    location: 26,
    contact: 26,
    needsLabel: 24,
    needsText: 28,
    cta: 28,
    headline: 54,
  },
};

// Export card dimensions for ShareNeedsModal
export { CARD_WIDTH, CARD_HEIGHT_POST, CARD_HEIGHT_STORY };

export const MultiShelterShareCard = forwardRef<HTMLDivElement, MultiShelterShareCardProps>(
  (
    {
      shelters,
      hideDisclaimer = false,
      customHeadline,
      sizeMode = "post",
      needsFontSize = "medium",
      backgroundDataUrl,
    },
    ref
  ) => {
    const shelterCount = shelters.length;
    const isCompact = sizeMode === "compact";
    const isStory = sizeMode === "story";
    const cardHeight = isStory ? CARD_HEIGHT_STORY : CARD_HEIGHT_POST;

    // Get font sizes based on layout and mode
    const getFontSizes = () => {
      const sizes = isStory ? FONT_SIZES_STORY : FONT_SIZES;
      let baseFonts;
      if (shelterCount === 1) baseFonts = sizes.single;
      else if (shelterCount === 2) baseFonts = sizes.double;
      else baseFonts = sizes.triple;
      
      // For compact mode - keep normal font sizes, the card will grow to fit content
      let fonts = { ...baseFonts };
      
      // For compact mode, slightly reduce needs text
      if (isCompact) {
        fonts.needsText = Math.round(fonts.needsText * 0.85);
      } else {
        // For non-compact modes with fixed height, apply font size adjustment
        const needsMultiplier = needsFontSize === "small" ? 0.8 : needsFontSize === "large" ? 1.2 : 1;
        fonts.needsText = Math.round(fonts.needsText * needsMultiplier);
      }
      
      return fonts;
    };
    const fonts = getFontSizes();

    // Parse formatted needs text
    // Supports: 
    // - Lines starting with "- " or "• " become bullet points
    // - Regular lines (from Enter key) become separate lines without bullets
    // - Text wrapped in **text** becomes bold
    // - Empty lines create visual spacing between paragraphs
    const parseFormattedNeeds = (needs: string): { text: string; isBullet: boolean; isEmpty: boolean }[] => {
      if (!needs) return [{ text: "Brak informacji o potrzebach", isBullet: false, isEmpty: false }];
      
      // Split by newlines, preserve empty lines for paragraph spacing
      const lines = needs.split('\n');
      
      return lines.map(line => {
        const trimmed = line.trim();
        // Empty lines create paragraph breaks
        if (trimmed.length === 0) return { text: "", isBullet: false, isEmpty: true };
        // Check if it's a bullet point
        if (trimmed.startsWith('- ') || trimmed.startsWith('• ')) {
          return { text: trimmed.slice(2).trim(), isBullet: true, isEmpty: false };
        }
        return { text: trimmed, isBullet: false, isEmpty: false };
      });
    };

    // Render text with **bold** support
    const renderFormattedText = (text: string) => {
      const parts = text.split(/(\*\*[^*]+\*\*)/g);
      return parts.map((part, idx) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={idx}>{part.slice(2, -2)}</strong>;
        }
        return <span key={idx}>{part}</span>;
      });
    };

    // Calculate relative weight for each shelter based on content length
    // Cards with more text get proportionally more space
    const calculateContentWeights = () => {
      if (shelterCount === 1) return [1];
      
      const lengths = shelters.map(s => (s.current_needs || "").length);
      const maxLength = Math.max(...lengths);
      const minLength = Math.min(...lengths);
      
      // If all have similar length, equal weights
      if (maxLength - minLength < 50) {
        return shelters.map(() => 1 / shelterCount);
      }
      
      // Calculate weights with more pronounced differences
      // Shortest gets base weight, longest gets up to 2x base
      const weights = lengths.map(len => {
        const normalized = (len - minLength) / (maxLength - minLength || 1);
        // Base weight + up to 1x extra based on content
        return 1 + normalized * 1.2;
      });
      
      // Normalize to sum to 1
      const weightSum = weights.reduce((a, b) => a + b, 0);
      return weights.map(w => w / weightSum);
    };
    
    const contentWeights = calculateContentWeights();

    // Render shelter card
    const renderShelterCard = (shelter: Shelter, index: number, flexWeight: number) => {
      const isUrgent = isUrgentNeedsActive(shelter);
      const { text: truncatedNeeds, hasMore } = truncateText(
        shelter.current_needs || "",
        getMaxCharsForLayout(shelterCount, isStory, isCompact)
      );
      const needsItems = parseFormattedNeeds(truncatedNeeds);
      const isSingle = shelterCount === 1;
      
      // Dynamic sizing based on content
      const needsLength = (shelter.current_needs || "").length;
      const isShortContent = needsLength < 80;
      const isMediumContent = needsLength >= 80 && needsLength < 200;

      // Format address
      const formatAddress = () => {
        const parts = [];
        if (shelter.address) parts.push(shelter.address);
        if (shelter.city) parts.push(shelter.city);
        return parts.join(", ");
      };

      return (
        <div
          key={shelter.id}
          style={{
            background: "#FFFFFF",
            borderRadius: isSingle ? 28 : 24,
            boxShadow: isSingle 
              ? "0 8px 32px rgba(45, 90, 61, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06)"
              : "0 6px 24px rgba(45, 90, 61, 0.1), 0 2px 6px rgba(0, 0, 0, 0.04)",
            display: "flex",
            flexDirection: "column",
            position: "relative",
            overflow: "hidden",
            // Auto-size based on content - no flex stretching
          }}
        >
          {/* PILNE badge - top right corner */}
          {isUrgent && (
            <div
              style={{
                position: "absolute",
                top: isSingle ? 20 : 14,
                right: isSingle ? 20 : 14,
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: "#EF4444",
                padding: isSingle ? "10px 16px" : "7px 14px",
                borderRadius: 10,
                boxShadow: "0 3px 10px rgba(239, 68, 68, 0.3)",
              }}
            >
              <AlertTriangle 
                style={{ 
                  width: isSingle ? 18 : 15, 
                  height: isSingle ? 18 : 15, 
                  color: "#FFFFFF",
                }} 
              />
              <span
                style={{
                  fontSize: isSingle ? 15 : 13,
                  fontWeight: 800,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "#FFFFFF",
                }}
              >
                Pilne!
              </span>
            </div>
          )}

          {/* Card content - MUCH larger padding for single shelter */}
          <div
            style={{
              padding: isStory
                ? (isSingle ? "56px 60px" : isShortContent ? "24px 36px" : isMediumContent ? "28px 40px" : "32px 36px")
                : (isSingle ? "48px 52px" : isShortContent ? "20px 28px" : isMediumContent ? "24px 32px" : "24px 28px"),
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Shelter name - improved typography */}
            <h2
              style={{
                fontSize: fonts.shelterName,
                fontWeight: 900,
                color: "#2D5A3D",
                fontFamily: "'Nunito', 'Quicksand', sans-serif",
                margin: 0,
                lineHeight: 1.25,
                letterSpacing: "-0.02em",
                paddingRight: isUrgent ? (isSingle ? 100 : 80) : 0,
              }}
            >
              {shelter.name}
            </h2>

            {/* Address and phone on one line */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: isSingle ? 16 : 8,
                marginTop: isSingle ? 14 : isShortContent ? 6 : 8,
                fontSize: fonts.location,
                color: "#6B7280",
              }}
            >
              {/* Address */}
              <div style={{ display: "flex", alignItems: "center", gap: 4, color: "#2D5A3D" }}>
                <MapPin style={{ width: isSingle ? 18 : 14, height: isSingle ? 18 : 14, flexShrink: 0 }} />
                <span style={{ fontWeight: 500 }}>{formatAddress()}</span>
              </div>
              
              {/* Phone - only first number if multiple, slightly smaller than address */}
              {shelter.phone && (
                <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: fonts.location * 0.9 }}>
                  <Phone style={{ width: isSingle ? 16 : 12, height: isSingle ? 16 : 12, flexShrink: 0 }} />
                  <span>{shelter.phone.split(/[,;]/)[0].trim()}</span>
                </div>
              )}
            </div>


            {/* POTRZEBNE TERAZ badge */}
            <div
              style={{
                marginTop: isSingle ? 14 : isShortContent ? 8 : 10,
                display: "inline-flex",
                alignItems: "center",
                gap: isSingle ? 8 : 6,
                background: "linear-gradient(135deg, #E8F5EA 0%, #D4EED8 100%)",
                padding: isSingle ? "10px 16px" : "5px 10px",
                borderRadius: isSingle ? 10 : 6,
                alignSelf: "flex-start",
              }}
            >
              <PawPrint 
                style={{ 
                  width: isSingle ? 18 : 12, 
                  height: isSingle ? 18 : 12, 
                  color: "#2D5A3D",
                }} 
              />
              <span
                style={{
                  fontSize: isSingle ? fonts.needsLabel - 4 : fonts.needsLabel - 8,
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: "#2D5A3D",
                }}
              >
                Aktualne potrzeby:
              </span>
            </div>

            {/* Needs as text blocks - auto-sized */}
            <div
              style={{
                marginTop: isSingle ? 40 : isShortContent ? 8 : shelterCount === 2 ? 12 : 6,
                display: "flex",
                flexDirection: "column",
                gap: isSingle ? 4 : 2,
              }}
            >
              {needsItems.map((item, idx) => {
                // Empty line creates visual spacing
                if (item.isEmpty) {
                  return <div key={idx} style={{ height: isSingle ? 12 : 8 }} />;
                }
                return (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 8,
                      fontSize: fonts.needsText,
                      color: "#374151",
                      lineHeight: 1.4,
                    }}
                  >
                    {item.isBullet && (
                      <span style={{ color: "#2D5A3D", fontWeight: 700 }}>●</span>
                    )}
                    <span>
                      {renderFormattedText(item.text)}
                    </span>
                  </div>
                );
              })}

            </div>

            {/* Update date at bottom */}
            {shelter.last_updated && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  marginTop: "auto",
                  paddingTop: isSingle ? 12 : isShortContent ? 6 : 8,
                  fontSize: isSingle ? 18 : 14,
                  color: "#9CA3AF",
                }}
              >
                <Calendar style={{ width: isSingle ? 14 : 12, height: isSingle ? 14 : 12, flexShrink: 0 }} />
                <span>
                  Aktualizacja: {new Date(shelter.last_updated).toLocaleDateString("pl-PL", { day: "numeric", month: "long", year: "numeric" })}
                </span>
              </div>
            )}
          </div>
        </div>
      );
    };

    // In compact mode, we use auto height
    const cardStyle = isCompact
      ? {
          width: CARD_WIDTH,
          minHeight: 600,
          backgroundColor: "#FFFFFF",
          fontFamily: "'Nunito', 'Quicksand', sans-serif",
          display: "flex" as const,
          flexDirection: "column" as const,
          position: "relative" as const,
          overflow: "hidden" as const,
        }
      : {
          width: CARD_WIDTH,
          height: cardHeight,
          backgroundColor: "#FFFFFF",
          fontFamily: "'Nunito', 'Quicksand', sans-serif",
          display: "flex" as const,
          flexDirection: "column" as const,
          position: "relative" as const,
          overflow: "hidden" as const,
        };

    const bgImage = backgroundDataUrl || shareCardBg;

    const backgroundCrossOrigin: CrossOrigin | undefined = (() => {
      // Avoid setting crossOrigin for data/blob/same-origin URLs (can cause iOS load quirks)
      if (!bgImage || typeof window === "undefined") return undefined;
      if (bgImage.startsWith("data:") || bgImage.startsWith("blob:") || bgImage.startsWith("/")) return undefined;
      try {
        const origin = new URL(bgImage, window.location.href).origin;
        return origin !== window.location.origin ? ("anonymous" as const) : undefined;
      } catch {
        return undefined;
      }
    })();

    return (
      <div
        ref={ref}
        style={cardStyle}
      >
        {/* iOS-safe background: html-to-image often drops CSS background-image on mobile Safari */}
        <img
          src={bgImage}
          alt=""
          draggable={false}
          data-export-bg="true"
          crossOrigin={backgroundCrossOrigin}
          loading="eager"
          decoding="sync"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center",
            zIndex: 0,
          }}
        />

        <div
          style={{
            position: "relative",
            zIndex: 1,
            width: "100%",
            height: isCompact ? "auto" : "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
        {/* Header - subtle brand in corner */}
        <div
          style={{
            padding: isStory 
              ? "32px 60px 0"
              : "24px 50px 0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: isStory ? 10 : 8,
            flexShrink: 0,
          }}
        >
          {/* Brand icon - green rounded square with white paw */}
          <div
            style={{
              background: "#4E8A6E",
              borderRadius: isStory ? 14 : 12,
              width: isStory ? 52 : 44,
              height: isStory ? 52 : 44,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <PawPrint 
              style={{ 
                width: isStory ? 30 : 24, 
                height: isStory ? 30 : 24, 
                color: "#FFFFFF",
              }} 
            />
          </div>
          <span
            style={{
              color: "#4E8A6E",
              fontSize: isStory ? 26 : 22,
              fontWeight: 700,
              fontFamily: "'Nunito', 'Quicksand', sans-serif",
              letterSpacing: "-0.01em",
              opacity: 0.9,
            }}
          >
            GdzieSchronisko
          </span>
        </div>

        {/* Shelters List + footer (footer is now directly under cards) */}
        <div
          style={{
            flex: 1,
            padding: isStory
              ? shelterCount === 2
                ? "0 100px"
                : "0 80px"
              : shelterCount === 2
                ? "0 100px"
                : "0 80px",
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            justifyContent: isCompact ? "flex-start" : "center",
            marginTop: isCompact ? 0 : (isStory ? -80 : -60),
          }}
        >
          {/* Headline */}
          <h1
            style={{
              color: "#4E8A6E",
              fontSize: isCompact ? fonts.headline * 0.7 : fonts.headline + 10,
              fontWeight: 900,
              fontFamily: "'Nunito', 'Quicksand', sans-serif",
              lineHeight: 1.15,
              textAlign: "center",
              margin: 0,
              marginBottom: isStory ? (shelterCount === 3 ? 28 : 36) : (shelterCount === 3 ? 20 : 28),
              letterSpacing: "-0.02em",
            }}
          >
            {customHeadline || "Jak możesz pomóc?"}
          </h1>

          {/* Cards */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: isStory
                ? (shelterCount === 1 ? 28 : shelterCount === 2 ? 24 : 20)
                : (shelterCount === 1 ? 20 : shelterCount === 2 ? 18 : 14),
            }}
          >
            {shelters.map((shelter, index) => renderShelterCard(shelter, index, contentWeights[index]))}
          </div>

          {/* Footer - full width of cards */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: isStory ? 10 : 8,
              marginTop: isStory ? 18 : 14,
            }}
          >
            <div
              style={{
                background: "#4E8A6E",
                width: "100%",
                borderRadius: 9999,
                padding: isStory ? "8px 18px" : "6px 14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: isStory ? 8 : 6,
                boxSizing: "border-box",
              }}
            >
              <span
                style={{
                  fontSize: isStory ? fonts.cta - 6 : fonts.cta - 8,
                  color: "white",
                  fontWeight: 700,
                  fontFamily: "'Nunito', 'Quicksand', sans-serif",
                  whiteSpace: "nowrap",
                }}
              >
                GdzieSchronisko.pl
              </span>
              <span
                style={{
                  fontSize: isStory ? fonts.cta - 10 : fonts.cta - 12,
                  color: "rgba(255,255,255,0.85)",
                  fontWeight: 500,
                  fontFamily: "'Nunito', 'Quicksand', sans-serif",
                  whiteSpace: "nowrap",
                }}
              >
                - mapa schronisk i ich aktualnych potrzeb
              </span>
            </div>

            {!hideDisclaimer && (
              <p
                style={{
                  fontSize: isStory
                    ? (shelterCount === 1 ? 16 : shelterCount === 2 ? 14 : 12)
                    : (shelterCount === 1 ? 13 : shelterCount === 2 ? 12 : 11),
                  color: "#6B8A7A",
                  fontWeight: 500,
                  fontFamily: "'Nunito', 'Quicksand', sans-serif",
                  margin: 0,
                  textAlign: "center",
                  lineHeight: 1.3,
                }}
              >
                Przed pomocą skontaktuj się z organizacją
              </p>
            )}
          </div>
        </div>

        {/* Bottom padding */}
        <div style={{ height: isStory ? 40 : 32, flexShrink: 0 }} />

        </div>
      </div>
    );
  }
);

MultiShelterShareCard.displayName = "MultiShelterShareCard";
